import { LitElement, html, TemplateResult } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styles } from '../view/styles';
import { translateFn } from 'apps/webcomponents/src/utils/translator.helper';
import { ButtonFontSizes, ButtonHeights, ButtonTypes } from '../../gzButton/types';
import { ButtonColor, ButtonFill, ButtonType } from '../../gzIconButton/types';
import { WEB_COMPONENT_GZ_CLIP_CONTENT } from 'apps/webcomponents/src/webcomponents.constants';

@customElement(WEB_COMPONENT_GZ_CLIP_CONTENT)
export class GzClipContentWebComponent extends LitElement {
  @property({ type: Boolean, attribute: true, reflect: true })
  public expanded = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  public placeholderPadding = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  public hasTextActionButton = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  public breakWords = true;

  @query('[data-clip]')
  private clipElementRef: HTMLElement;

  @state()
  private showActionButton = false;

  static get styles() {
    return styles;
  }

  private resizeObserver: ResizeObserver;

  private hostWidth: number;

  firstUpdated(): void {
    this.showHideActionButton();
  }

  connectedCallback(): void {
    super.connectedCallback();

    this.resizeObserver = new ResizeObserver(entities => this.computeShowHideButtonOnResize(entities));

    this.resizeObserver.observe(this);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();

    this.resizeObserver.unobserve(this);
  }

  private computeShowHideButtonOnResize(entities: ResizeObserverEntry[]): void {
    const observedClipElement = entities[0];
    const { width } = observedClipElement.contentRect;
    const widthHasChanged = width !== this.hostWidth;

    if (widthHasChanged) {
      this.showHideActionButton();
    }

    this.hostWidth = width;
  }

  private showHideActionButton(): void {
    const { scrollHeight, clientHeight } = this.clipElementRef;

    this.showActionButton = scrollHeight > clientHeight;
  }

  private getActionButtonTemplate(): TemplateResult | string {
    if (this.showActionButton) {
      return this.hasTextActionButton
        ? html`${this.getTextActionButtonTemplate()}`
        : html`${this.getIconActionButtonTemplate()}`;
    }

    return '';
  }

  private getIconActionButtonTemplate(): TemplateResult {
    return html` <gz-icon-button
      class="clip-container__action-btn"
      @click=${() => (this.expanded = !this.expanded)}
      icon="${this.expanded ? 'ds-icon-arrow_up_collapse' : 'ds-icon-arrow_down_expand'}"
      buttonType=${ButtonType.ROUND}
      buttonFill=${ButtonFill.QUIET}
      buttonColor=${ButtonColor.SECONDARY}
      size="18"
      fontSize="18"
    ></gz-icon-button>`;
  }

  private getTextActionButtonTemplate(): TemplateResult {
    return html`<gz-button
      id="action-button"
      buttonheight=${ButtonHeights.SMALL}
      buttonfontsize=${ButtonFontSizes.SMALL}
      @click=${() => (this.expanded = !this.expanded)}
      type=${ButtonTypes.QUIET}
      lowercase
    >
      ${this.expanded ? translateFn('Show less') : translateFn('Show more')}
    </gz-button>`;
  }

  render() {
    const classes = {
      'clip__item--clip': !this.expanded,
      'placeholder-padding': !this.showActionButton && this.placeholderPadding,
    };

    return html`
      <div class="clip-container">
        <div class="clip__item ${classMap(classes)}" data-clip="true">
          <slot></slot>
        </div>

        ${this.getActionButtonTemplate()}
      </div>
    `;
  }
}
